import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { ConnectorPanel } from '../components/dataIngestion/ConnectorDetail';
import { ConnectorWorkspace } from '../components/dataIngestion/ConnectorWorkspace';
import { GlobalTriggerDialog } from '../components/dataIngestion/GlobalTriggerDialog';
import { ingest, tabular } from '../components/dataIngestion/ingestionTokens';
import { PageHeader } from '../components/ui';
import { AnnouncementCandidatesPanel } from '../components/AnnouncementCandidatesPanel';
import { IngestionJobsPanel } from '../components/IngestionJobsPanel';
import {
  TriggerIngestionPayload,
  integrationApiService,
} from '../services/integrationApi';

type PageTab = 'connectors' | 'signals' | 'jobs';

function parseTab(value: string | null): PageTab {
  if (value === 'signals' || value === 'jobs' || value === 'connectors') return value;
  return 'connectors';
}

function parsePanel(value: string | null): ConnectorPanel {
  if (value === 'runs' || value === 'categories' || value === 'prices' || value === 'rebates' || value === 'overview') return value;
  return 'overview';
}

function isOpenSignal(status?: string): boolean {
  return ['new', 'watching', 'scrape_queued'].includes(String(status || ''));
}

function withinHours(iso: string | undefined, hours: number): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  return Date.now() - date.getTime() <= hours * 60 * 60 * 1000;
}

export const DataIngestionPage: React.FC = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggerResult, setTriggerResult] = useState<{ executionMethod?: string; runCommand?: string } | null>(null);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [triggerErrorJobId, setTriggerErrorJobId] = useState<string | null>(null);

  const tab = parseTab(searchParams.get('tab'));
  const brandId = searchParams.get('brand');
  const panel = parsePanel(searchParams.get('panel'));

  const updateParams = useCallback((next: { tab?: PageTab; brand?: string | null; panel?: ConnectorPanel }) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const currentTab = parseTab(prev.get('tab'));
      const currentBrand = prev.get('brand');
      const currentPanel = parsePanel(prev.get('panel'));
      const nextTab = next.tab ?? currentTab;
      const nextBrand = next.brand === undefined ? currentBrand : next.brand;
      const nextPanel = next.panel ?? currentPanel;
      if (nextTab === 'connectors') params.delete('tab');
      else params.set('tab', nextTab);
      if (nextBrand) params.set('brand', nextBrand);
      else params.delete('brand');
      if (nextPanel === 'overview') params.delete('panel');
      else params.set('panel', nextPanel);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const brandFromState = (location.state as { brandId?: string } | null)?.brandId;
    if (!brandFromState) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('brand', brandFromState);
      params.delete('tab');
      return params;
    }, { replace: true, state: {} });
  }, [location.state, setSearchParams]);

  const { data: brands = [] } = useQuery({
    queryKey: ['ingestion-brands'],
    queryFn: () => integrationApiService.getIngestionBrands(),
    enabled: !!currentUser,
    refetchInterval: 10000,
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ['ingestion-jobs'],
    queryFn: () => integrationApiService.getIngestionJobs(undefined, 50),
    enabled: !!currentUser,
    refetchInterval: 8000,
  });

  const { data: announcements = [] } = useQuery({
    queryKey: ['announcement-candidates'],
    queryFn: () => integrationApiService.fetchAnnouncementCandidates(undefined, 80),
    enabled: !!currentUser,
  });

  const { data: runningJobs = [] } = useQuery({
    queryKey: ['ingestion-jobs-running'],
    queryFn: () => integrationApiService.getRunningIngestionJobs(),
    enabled: !!currentUser,
    refetchInterval: 8000,
  });

  const openSignals = announcements.filter((c: { status?: string }) => isOpenSignal(c.status));
  const recentJobs = jobs.filter((job) => withinHours(job.createdAt || job.startedAt, 48));
  const cloudRunAvailable = useMemo(() => brands.some((b) => b.cloudRunEnabled), [brands]);
  const runningBrand = brands.find((brand) => brand.isRunning || (brand.stats?.running || 0) > 0);
  const hasRunningJob = runningJobs.length > 0 || Boolean(runningBrand);
  const runningBrandName = runningJobs[0]?.brandName || runningBrand?.name;
  const triggerMutation = useMutation({
    mutationFn: ({ brandId: id, payload }: { brandId: string; payload: TriggerIngestionPayload }) =>
      integrationApiService.triggerIngestion(id, payload),
    onSuccess: (data) => {
      setTriggerResult(data);
      setTriggerError(null);
      setTriggerErrorJobId(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] });
    },
    onError: (error: { message?: string; data?: { runningJobId?: string } }) => {
      setTriggerResult(null);
      setTriggerError(error?.message || 'Failed to start ingestion job');
      setTriggerErrorJobId(error?.data?.runningJobId || null);
    },
  });

  const priceRefreshMutation = useMutation({
    mutationFn: ({ brandId: id, region }: { brandId?: string; region?: 'US' | 'CA' }) =>
      integrationApiService.triggerPriceRefresh(id, region),
    onSuccess: (data) => {
      setTriggerResult(data);
      setTriggerError(null);
      setTriggerErrorJobId(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
    },
    onError: (error: { message?: string; data?: { runningJobId?: string } }) => {
      setTriggerResult(null);
      setTriggerError(error?.message || 'Failed to start price refresh');
      setTriggerErrorJobId(error?.data?.runningJobId || null);
    },
  });

  const marketPricingMutation = useMutation({
    mutationFn: ({ brandId: id }: { brandId?: string }) =>
      integrationApiService.triggerMarketPricingRefresh(id),
    onSuccess: () => {
      setTriggerResult({ executionMethod: 'immediate' });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
    },
    onError: (error: { message?: string }) => {
      setTriggerResult(null);
      setTriggerError(error?.message || 'Failed to start market pricing refresh');
    },
  });

  const stockRefreshMutation = useMutation({
    mutationFn: ({ brandId: id, region }: { brandId?: string; region?: 'US' | 'CA' }) =>
      integrationApiService.triggerStockRefresh(id, region),
    onSuccess: () => {
      setTriggerResult({ executionMethod: 'cloud_run' });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
    },
    onError: (error: { message?: string; data?: { runningJobId?: string } }) => {
      setTriggerResult(null);
      setTriggerError(error?.message || 'Failed to start stock refresh');
      setTriggerErrorJobId(error?.data?.runningJobId || null);
    },
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] });
    queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
    queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
    queryClient.invalidateQueries({ queryKey: ['announcement-candidates'] });
  };

  const closeTrigger = () => {
    setTriggerOpen(false);
    setTriggerResult(null);
    setTriggerError(null);
    setTriggerErrorJobId(null);
  };

  const tabs: Array<{ id: PageTab; label: string; count: number }> = [
    { id: 'connectors', label: 'Vendor connectors', count: brands.length },
    { id: 'signals', label: 'Signals & watchlist', count: openSignals.length },
    { id: 'jobs', label: 'Ingestion jobs', count: recentJobs.length },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: ingest.font.sans,
      }}
    >
      <Box
        sx={{
          padding: { xs: '18px 16px 0', sm: '22px 32px 0' },
          display: 'flex',
          flexDirection: 'column',
          background: ingest.color.surface,
          borderBottom: `1px solid ${ingest.color.border}`,
        }}
      >
        <PageHeader
          title="Data Ingestion"
          description="Monitor vendor connectors and signals, and start ingestion runs."
          actions={
            <>
              <Button variant="outlined" onClick={refreshAll}>
                Refresh
              </Button>
              <Tooltip
                title={
                  hasRunningJob
                    ? `An ingestion job is running${runningBrandName ? ` for ${runningBrandName}` : ''}. Open this to force a start if it is stuck.`
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => setTriggerOpen(true)}
                  >
                    Trigger ingestion
                  </Button>
                </span>
              </Tooltip>
            </>
          }
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', overflowX: 'auto' }}>
          {tabs.map((entry) => {
            const active = tab === entry.id;
            return (
              <Box
                key={entry.id}
                component="button"
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => updateParams({ tab: entry.id })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  height: 38,
                  padding: '0 14px',
                  border: 0,
                  borderBottom: `2px solid ${active ? ingest.color.accent : 'transparent'}`,
                  background: 'transparent',
                  fontFamily: ingest.font.sans,
                  fontSize: '13px',
                  fontWeight: active ? 600 : 500,
                  color: active ? ingest.color.ink : ingest.color.muted,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: ingest.color.ink },
                }}
              >
                <span>{entry.label}</span>
                <Box
                  component="span"
                  sx={{
                    height: 18,
                    minWidth: 18,
                    padding: '0 5px',
                    borderRadius: '9px',
                    background: active ? ingest.color.accentTint : ingest.color.rowOpen,
                    color: active ? ingest.color.accentHover : ingest.color.dim,
                    fontSize: '11px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    textAlign: 'center',
                    ...tabular,
                  }}
                >
                  {entry.count}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, padding: { xs: '16px 16px 24px', sm: '20px 32px 32px' } }}>
        {tab === 'connectors' && (
          <ConnectorWorkspace
            selectedBrandId={brandId}
            panel={panel}
            onSelectBrand={(id) => updateParams({ brand: id })}
            onPanelChange={(next) => updateParams({ panel: next })}
            keyboardEnabled={tab === 'connectors' && !triggerOpen}
            hasRunningJob={hasRunningJob}
            runningBrandName={runningBrandName}
          />
        )}

        {tab === 'signals' && <AnnouncementCandidatesPanel />}

        {tab === 'jobs' && <IngestionJobsPanel embedded onTrigger={() => setTriggerOpen(true)} />}
      </Box>

      <GlobalTriggerDialog
        open={triggerOpen}
        onClose={closeTrigger}
        onTrigger={(id, payload) => triggerMutation.mutate({ brandId: id, payload })}
        onTriggerPriceRefresh={(id, region) => priceRefreshMutation.mutate({ brandId: id, region })}
        onTriggerMarketPricing={(id) => marketPricingMutation.mutate({ brandId: id })}
        onTriggerStockRefresh={(id, region) => stockRefreshMutation.mutate({ brandId: id, region })}
        isPending={
          triggerMutation.isPending ||
          priceRefreshMutation.isPending ||
          marketPricingMutation.isPending ||
          stockRefreshMutation.isPending
        }
        cloudRunAvailable={cloudRunAvailable}
        triggerResult={triggerResult}
        errorMessage={triggerError}
        errorJobId={triggerErrorJobId}
        brands={brands}
        initialBrandId={brandId || undefined}
        hasRunningJob={hasRunningJob}
        runningBrandName={runningBrandName}
      />
    </Box>
  );
};

export default DataIngestionPage;
